import {
  faAngleDown,
  faCheck,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  ListboxSelectedOption,
} from '@headlessui/react';
import noop from 'lodash/noop';
import React from 'react';
import styled, { css } from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import {
  BORDER_GRAY,
  COOL_GRAY100,
  HOVER,
  TEXT_DISABLED,
  WHITE,
} from '../../../theme';
import { EventNames } from '../../../utils/trackingEvents';
import Text, { TextColor, TextKind } from '../Text';

const StyledListBoxContainer = styled.div<{ $fullWidth: boolean }>`
  display: inline-flex;
  position: relative;
  ${({ $fullWidth }) => {
    if ($fullWidth) {
      return css`
        flex: 1;
      `;
    }
    return '';
  }}
`;

const StyledListBoxButton = styled(ListboxButton)<{ $fullWidth: boolean }>`
  ${({ $fullWidth }) => {
    if ($fullWidth) {
      return css`
        width: 100%;
      `;
    }
    return '';
  }}
  display: inline-flex;
  padding: 8px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  border-radius: 4px;
  border: 1px solid ${BORDER_GRAY};
  background: ${WHITE};

  cursor: pointer;

  &:disabled {
    cursor: default;

    color: ${TEXT_DISABLED};
    background: ${COOL_GRAY100};
  }
`;

const StyledAbsolute = styled.div`
  position: absolute;
  max-width: calc(100% - 35px);
`;

const StyledHidden = styled.div`
  &,
  & > * {
    visibility: hidden;
    height: 0px;
  }
`;

const StyledListBoxOptions = styled(ListboxOptions)`
  position: absolute;
  top: calc(100% + 4px);

  display: flex;
  flex-direction: column;

  width: 100%;
  padding: 2px 0px;
  margin: 0;

  border-radius: 4px;
  border: 1px solid ${BORDER_GRAY};
  background: ${WHITE};
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.1);

  z-index: 10;

  max-height: 200px;
  overflow-y: auto;
`;

const StyledListBoxOption = styled.li<{ $active?: boolean }>`
  list-style: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  gap: 8px;

  white-space: nowrap;

  ${({ $active }) => ($active ? `background: ${HOVER};` : '')};
`;

export type OptionType<
  T = string | boolean | number | undefined | number[] | null,
> = {
  label: string;
  value: T;
};

type MultiSelectOnChange = (value: OptionType[], name: string) => void;

type SingleSelectOnChange = (value: OptionType, name: string) => void;
/**
 * StyledAbsolute, StyledHidden Explanation:
 *
 * The ask from design as well as the default behavior of <select> is for the
 * Listbox Button to take the width of the widest option. To accomplish this,
 * we render all of the options in a hidden div at 0 height so they only affect
 * the horizontal layout. Then we just render the value of the selected option
 * with absolute positioning so it doesn't get affected by the hidden options
 */
type SelectProps =
  | {
      id?: string;
      value: OptionType;
      onChange: SingleSelectOnChange;
      options?: OptionType[];
      disabled?: boolean;
      name: string;
      onOpenCallback?: (name: string) => void;
      testId?: string;
      fullWidth?: boolean;
      clearable?: boolean;
      onClear?: () => void;
      multiple?: false;
      maxSelected?: never;
      placeholder?: string;
      trackingData?: TrackingEventData;
      onBlur?: () => void;
    }
  | {
      id?: string;
      value: OptionType[];
      onChange: MultiSelectOnChange;
      options?: OptionType[];
      disabled?: boolean;
      name: string;
      onOpenCallback?: (name: string) => void;
      testId?: string;
      fullWidth?: boolean;
      clearable?: boolean;
      onClear?: () => void;
      multiple: true;
      maxSelected?: number;
      placeholder?: string;
      trackingData?: TrackingEventData;
      onBlur?: () => void;
    };

export const Select: React.FC<SelectProps & React.PropsWithChildren> = ({
  id,
  value,
  onChange,
  options,
  disabled,
  name,
  fullWidth = false,
  onOpenCallback = noop,
  clearable = false,
  onClear = noop,
  multiple = false,
  maxSelected,
  placeholder,
  trackingData,
  onBlur,
  testId,
}) => {
  const { track } = useTracking();
  const hasMultipleValues = Array.isArray(value);

  const hasReachedMaxSelected =
    maxSelected && hasMultipleValues && value.length >= maxSelected;

  let hasValue = false;

  if (hasMultipleValues) {
    hasValue = value.length > 0;
  } else {
    hasValue = value.value !== undefined;
  }

  const handleOpen = (name: string) => {
    onOpenCallback(name);
    if (trackingData) {
      track(EventNames.DropDownMenuClick, trackingData);
    }
  };

  const handleChange = (val: OptionType) => {
    if (Array.isArray(val)) {
      (onChange as MultiSelectOnChange)(val, name);
    } else {
      (onChange as SingleSelectOnChange)(val, name);
    }
    if (trackingData) {
      track(EventNames.DropDownMenuItemClick, {
        ...trackingData,
        event_label: val.label,
      });
    }
  };

  const labelText = hasMultipleValues
    ? value.map((v) => v.label).join(', ')
    : value.label;
  return (
    <Listbox
      value={value}
      onChange={handleChange}
      disabled={disabled}
      multiple={multiple}
    >
      <StyledListBoxContainer $fullWidth={fullWidth}>
        <StyledListBoxButton
          $fullWidth={fullWidth}
          onMouseDown={() => handleOpen(name)}
          data-testid={testId || `listboxButton-${name}`}
          id={id}
          onBlur={onBlur}
        >
          {/* See comment above component definition for explanation on the following */}
          <StyledAbsolute>
            {placeholder && (
              <ListboxSelectedOption
                options={null}
                placeholder={
                  <Text style={{ opacity: 0.5 }}>{placeholder}</Text>
                }
              />
            )}
            <Text
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              kind={TextKind.BodyM}
              color={
                disabled || !hasValue ? TextColor.Disabled : TextColor.Primary
              }
            >
              {labelText}
            </Text>
          </StyledAbsolute>
          <StyledHidden aria-hidden>
            {options?.map((option) => (
              <Text key={String(option.value)} kind={TextKind.BodyM}>
                {option.label}
              </Text>
            ))}
          </StyledHidden>
          {clearable && hasValue ? (
            <FontAwesomeIcon
              data-testid={`listboxButton-clearSelection-${name}`}
              fontSize="16px"
              icon={faXmark}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            />
          ) : (
            <FontAwesomeIcon fontSize="16px" icon={faAngleDown} />
          )}
        </StyledListBoxButton>

        <StyledListBoxOptions>
          {options?.map((option) => {
            const disabledOption =
              hasReachedMaxSelected &&
              !value.some((v) => v.value === option.value);
            return (
              <ListboxOption
                key={String(option.value)}
                value={option}
                as={React.Fragment}
                disabled={!!disabledOption}
              >
                {({ selected, focus }) => {
                  const isSelected =
                    selected ||
                    (Array.isArray(value) &&
                      value.some((v) => v.value === option.value));
                  return (
                    <StyledListBoxOption $active={focus || isSelected}>
                      {isSelected && <FontAwesomeIcon icon={faCheck} />}
                      <Text
                        kind={TextKind.BodyM}
                        color={
                          option.value === undefined
                            ? TextColor.Disabled
                            : TextColor.Primary
                        }
                      >
                        {option.label}
                      </Text>
                    </StyledListBoxOption>
                  );
                }}
              </ListboxOption>
            );
          })}
        </StyledListBoxOptions>
      </StyledListBoxContainer>
    </Listbox>
  );
};
